import { type PropsWithChildren, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useAuth } from "../auth/AuthContext";
import { signOut } from "../lib/auth";

const DAGRAPPORT_GPT_URL =
  "https://chatgpt.com/g/g-6a8b3b73bdb48191a6165d4eff945be5-ams11-productie";
type Theme = "light" | "dark";

function initialTheme(): Theme {
  try {
    return localStorage.getItem("dbvdw-theme") === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function AppShell({ children }: PropsWithChildren) {
  const { user } = useAuth();
  const location = useLocation();
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("dbvdw-theme", theme);
    } catch { /* optional */ }
  }, [theme]);

  useEffect(() => {
    document.body.classList.toggle(
      "planning-page",
      location.pathname.startsWith("/planning"),
    );
    return () => document.body.classList.remove("planning-page");
  }, [location.pathname]);

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      globalThis.location.assign("/login/");
    }
  }

  return (
    <>
      <img
        className="app-watermark"
        src="/assets/logo.svg"
        alt=""
        aria-hidden="true"
        width="256"
        height="256"
      />
      <header className="app-header">
        <div className="shell">
          <span className="app-header__user">{user?.email ?? ""}</span>
          <NavLink className="app-brand" to="/home/">
            de Boer &amp; v/d Weijde
          </NavLink>
          <div className="app-header__meta">
            <button
              type="button"
              className="secondary theme-toggle icon-btn"
              aria-label="Thema wisselen"
              onClick={() => setTheme((current) => current === "dark" ? "light" : "dark")}
            >
              <span aria-hidden="true">{theme === "dark" ? "◑" : "◐"}</span>
            </button>
            <button
              type="button"
              className="secondary icon-btn"
              aria-label="Afmelden"
              title="Afmelden"
              disabled={signingOut}
              onClick={handleSignOut}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <nav className="app-nav" aria-label="Hoofdnavigatie">
        <div className="shell">
          <NavLink to="/home/">Dagverslagen</NavLink>
          <NavLink to="/preplanning/">Voorplanning</NavLink>
          <a
            href={DAGRAPPORT_GPT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Nieuw verslag
          </a>
          <NavLink to="/dwb/">Dont Walk By</NavLink>
          <NavLink to="/planning/">Planning</NavLink>
        </div>
      </nav>

      <div className="app-main">{children}</div>
    </>
  );
}
